import {Injectable, Injector} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {HttpInterceptorService} from '@ispm/core/services/http-interceptor.service';
import {CurationService} from '../../../app/curation/services/curation.service';
import {PathwaysExecutionData} from '@ispm-client/modules/pathways/models/pathways-execution-data';
import * as _ from 'lodash';
import {catchError, map} from 'rxjs/operators';
import {CurationItemId, failedToReadCurationItemMessage} from '@ispm-client/modules/pathways/models';
import {ProcessInputDataUtils} from '@ispm-client/modules/process-executor';
import {CurationClinicalKey} from '@ispm-shared/modules/pathways';
import {NodeDependencies} from "@ispm/shared/bpmn/models/node-dependencies";
import {of} from "rxjs";
import {SetErrorAction} from "@ispm/shared/store/actions/error.actions";
import {BrowserCacheService} from "@ispm/core/services/browser-cache.service";

@Injectable()
export class PathwaysService {

  private _browserCacheService: BrowserCacheService;

  constructor(private _http: HttpInterceptorService,
              private injector: Injector, browserCacheService: BrowserCacheService) {
      this._browserCacheService = browserCacheService;
    }

    private get curationService(): CurationService {
        return this.injector.get(CurationService);
    }

    getClinicalTrials(
        deploymentId: string,
        clinicalTrialsStatuses: string,
        placementStatuses: string,
        bpmnLinkedResourcesType: string,
        bpmnConnectingResourcesType: string,
        knowledgeBase: string,
        clinicalTrialSource: string[],
        clinicalTrialStatusSearchParams: string): Observable<any> {
        let context = !!clinicalTrialSource ? clinicalTrialSource : knowledgeBase;
        clinicalTrialStatusSearchParams = clinicalTrialStatusSearchParams || '';
        return this._http.get(
            `/pathways/clinicalTrials?deploymentId=${deploymentId}&clinicalTrialsStatuses=${clinicalTrialsStatuses}&clinicalTrialStatusSearchParams=${clinicalTrialStatusSearchParams}&placementStatuses=${placementStatuses}&bpmnLinkedResourcesType=${bpmnLinkedResourcesType}&bpmnConnectingResourcesType=${bpmnConnectingResourcesType}&knowledgeBase=${context}&cacheId=${this._browserCacheService.getCacheId()}`
        );
    }

    searchPathwaysExecutionData(
      knowledgeBase: string,
      bpmnType: string,
      curationClinicalKeys: any,
      statuses: string,
      orgId: string
    ): Observable<PathwaysExecutionData> {
      return this.curationService.searchCurationInfo(
        knowledgeBase,
        bpmnType,
        {
          ...this.curationClinicalKeysToDictionary(curationClinicalKeys), ...{'status:exact': statuses},
          joinToBatchQuery: true,
          kb: knowledgeBase,
          _fieldslist: 'content,authorization',
          latest: true
        })
        .pipe(
          catchError(error => Observable.throwError({ message: error })),
          map(result => {
             if (result.length > 1) {
                // select this one, which relates to the current organization
               const foundRes = result.find(item => {
                  return !!(_.get(item, 'curationData.authorization', [])
                    .find(auth => _.get(auth, 'resourceType', '').toLowerCase() === 'organization' && _.get(auth, 'reference', '') === orgId));
               });
               return !!foundRes ? foundRes : result[0];
             }
             return result[0];
          }),
          map(result => this.curationDataToPathwaysExecutiondata(result))
        );
    }

    getPathwaysExecutionData(curationItemId: CurationItemId, knowledgeBase: string, bpmnType: string): Observable<PathwaysExecutionData> {
        return this.curationService.getCurationItemV1(
            curationItemId.id, curationItemId.revisionId, knowledgeBase, bpmnType).pipe(
            map(result => this.curationDataToPathwaysExecutiondataV1Api(result),
                catchError(error => of(new SetErrorAction({
                    error: error.message,
                    devMessage: 'getPathwaysExecutionData: Failed to get curation item',
                    userMessage: failedToReadCurationItemMessage
                }))))
        );
    }

    private curationClinicalKeysToDictionary(clinicalKeys: CurationClinicalKey[]): Object {
        const dictionary = {};
        clinicalKeys.forEach(clinicalKey => {
            dictionary[clinicalKey.target] = clinicalKey.key;
        });
        return dictionary;
    }


    private curationDataToPathwaysExecutiondata(data: any): PathwaysExecutionData {
        return {
            patientData: ProcessInputDataUtils.convertFieldsNamesToEmptyInputData(_.get(data, 'curationData.content.bpmn.fields', [])),
            bpmnIdentifier: _.get(data, 'curationData.content.bpmn.bpmnIdentifier', null),
            nodes: _.get(data, 'curationData.content.bpmn.nodes', null),
            curationItemId: {
                id: _.get(data, 'curationItemId', null),
                revisionId: _.get(data, 'revisionId', null)
            },
            nodeSelectionDependencies: this.toNodeDepedencies(_.get(data, 'curationData.content.bpmn.nodeSelectionDependencies'))
        };
    }

    private toNodeDepedencies(value: any[]): NodeDependencies {
        if (!_.isEmpty(value)) {
            return {
                dependencies: this.toMap(value['dependencies']),
                replacements: this.toMap(value['replacements'])
            }
        }
        return null;
    }

    private toMap(value: any[]): Map<string, Set<string>> {
        let result = new Map<string, Set<string>>();
        if (!_.isEmpty(value)) {
            value.forEach(element => {
                const key = element[0];
                const deps = new Set<string>(element[1]);
                result.set(key, deps);
            });
        }
        return result;
    }

    private curationDataToPathwaysExecutiondataV1Api(data: any): PathwaysExecutionData {
        return {
            patientData: ProcessInputDataUtils.convertFieldsNamesToEmptyInputData(_.get(data, 'curationData.content.bpmn.fields', [])),
            bpmnIdentifier: _.get(data, 'curationData.content.bpmn.bpmnIdentifier', null),
            nodes: _.get(data, 'curationData.content.bpmn.nodes', null),
            curationItemId: {
                id: _.get(data, 'curationItem.id', null),
                revisionId: _.get(data, 'curationData.revisionId', null)
            }
        };
    }
}
